import "./App.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
// theme
import { theme } from "./theme/Maintheme";
// Components
import Layout from "./components/AppLayout";

import { AppStateContext } from "context/appState.context";
import AlertNotification from "components/AlertNotification/AlertNotification";
import { useEffect, useMemo, useState } from "react";
import { AlertNotificationConfig } from "object-models/shared/alertNotification-config.model";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import HomePage from "pages/HomePage";
import Landing from "pages/LandingPage";
import Glossary from "pages/Glossary/Glossary";
import PortfolioView from "pages/PortfolioView/PortfolioView";
import { Utilities } from "utilities/utilities";
import SessionTimeoutModel from "components/SessionTimeout/SessionTimeout.model";
import authOperations from "configs/authService";
import { msalInstance } from "msalInstance";
import LocationLayout from "pages/LocationLayout/LocationLayout";
import { BreadcrumbProvider } from "context/breadcrumb.context";
import { ExportPackProvider } from "context/exportPack.context";
import ExportPackDrawer from "components/ExportPackDrawer/ExportPackDrawer";
import NotificationHelper from "services/notification-helper";

const CURRENCY_STORAGE_KEY = "app_currency";

function App() {
  const notificationModelDefault: AlertNotificationConfig = {
    message: "",
    open: false,
    severity: undefined,
  };
  const [notificationConfig, setNotificationConfig] = useState(
    notificationModelDefault
  );
  const [clientId, setClientId] = useState(0);
  const [userRole, setUserRole] = useState("");
  const [locationName, setLocationName] = useState("");
  const [currency, setCurrency] = useState(
    () => localStorage.getItem(CURRENCY_STORAGE_KEY) ?? "",
  );
  const [discountFactor, setDiscountFactor] = useState(0);
  const [averageAnnualGrowthRate, setAverageAnnualGrowthRate] = useState(0);
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  useEffect(() => {
    if (accounts.length > 0) msalInstance.setActiveAccount(accounts[0]);
  }, [accounts]);

  useEffect(() => {
    if (currency) {
      localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
      return;
    }

    localStorage.removeItem(CURRENCY_STORAGE_KEY);
  }, [currency]);

  useEffect(() => {
    NotificationHelper.setNotificationDispatcher(setNotificationConfig);
    return () => {
      NotificationHelper.setNotificationDispatcher();
    };
  }, []);

  const memoizedContextValue = useMemo(() => {
    return {
      notificationConfig,
      setNotificationConfig,
      clientId,
      setClientId,
      userRole,
      setUserRole,
      locationName,
      setLocationName,
      discountFactor,
      setDiscountFactor,
      averageAnnualGrowthRate,
      setAverageAnnualGrowthRate,
      currency,
      setCurrency
    };
  }, [
    notificationConfig,
    setNotificationConfig,
    clientId,
    setClientId,
    userRole,
    setUserRole,
    locationName,
    setLocationName,
    discountFactor,
    setDiscountFactor,
    averageAnnualGrowthRate,
    setAverageAnnualGrowthRate,
    currency,
    setCurrency
  ]);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const defaultExpireTime = 20000;
  const [timeLeft, setTimeLeft] = useState(defaultExpireTime);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const tokenTimeLeft = Utilities.timeLeftInTokenExpiration();
      if (tokenTimeLeft <= 5 && tokenTimeLeft > 1) {
        setTimeLeft(1);
      } else if (tokenTimeLeft <= 1) {
        setTimeLeft(0);
      }
    };
    checkTokenExpiration();
    const intervalId = window.setInterval(checkTokenExpiration, 1000 * 60);
    return () => window.clearInterval(intervalId);
  }, []);

  function SetWarningModel() {
    if (timeLeft === 1) {
      setIsWarningModalOpen(true);
    } else if (timeLeft === 0) {
      handleLogout();
    }
  }

  useEffect(() => {
    SetWarningModel();
  }, [timeLeft]);

  function refreshToken() {
    authOperations.getRefreshToken();
    setIsWarningModalOpen(false);
    setTimeLeft(defaultExpireTime);
  }

  return (
    <>
      {isWarningModalOpen && (
        <SessionTimeoutModel
          onContinueSession={() => refreshToken()}
          onLogOut={() => handleLogout()}
        />
      )}
      <ThemeProvider theme={theme}>
        <AppStateContext.Provider value={memoizedContextValue}>
          <BreadcrumbProvider>
          <ExportPackProvider>
          <AlertNotification />
          <ExportPackDrawer />
          <Layout
            signOut={handleLogout}
            isAuthorized={isAuthenticated}
            account={accounts[0]}
          >
            <Routes>
              <Route
                path="/"
                element={
                  <UnauthenticatedTemplate>
                    <Landing />
                  </UnauthenticatedTemplate>
                }
              />
              <Route
                path="/glossary"
                element={
                  <UnauthenticatedTemplate>
                    <Glossary />
                  </UnauthenticatedTemplate>
                }
              />

              <Route
                path="/Locations"
                element={
                  <AuthenticatedTemplate>
                    <HomePage></HomePage>
                  </AuthenticatedTemplate>
                }
              />

              <Route
                path="/Clients"
                element={
                  <AuthenticatedTemplate>
                    <HomePage></HomePage>
                  </AuthenticatedTemplate>
                }
              />

              <Route
                path="/Organizations"
                element={
                  <AuthenticatedTemplate>
                    <HomePage></HomePage>
                  </AuthenticatedTemplate>
                }
              />

              <Route
                id="10"
                path="/Location/:LocationId/*"
                element={
                  <AuthenticatedTemplate>
                    <LocationLayout></LocationLayout>
                  </AuthenticatedTemplate>
                }
              />
              <Route
                id="11"
                path="/Locations/Portfolioview/*"
                element={
                  <AuthenticatedTemplate>
                    <LocationLayout></LocationLayout>
                  </AuthenticatedTemplate>
                }
              />
              <Route
                id="12"
                path="/Locations/PortfolioView"
                element={
                  <AuthenticatedTemplate>
                    <PortfolioView />
                  </AuthenticatedTemplate>
                }
              />
              
            </Routes>
          </Layout>
          </ExportPackProvider>
          </BreadcrumbProvider>
        </AppStateContext.Provider>
      </ThemeProvider>
    </>
  );
}

export default App;